import { createTheme } from '@mui/material/styles';
import { frFR as coreFrFR } from '@mui/material/locale';
import { frFR as dataGridFrFR } from '@mui/x-data-grid/locales';
import { appleTokens as t, tint } from './appleTokens';

// Thème MUI global de l'app web, construit sur les tokens partagés : tout composant MUI utilisé "tel
// quel" (Button, TextField, Dialog, Table, DataGrid, Tabs, Menu…) prend automatiquement le même style
// que les composants maison du kit ERP (components/ERP/ui).
//
// `shape.borderRadius` reste à 4 (valeur MUI par défaut) : les `sx={{ borderRadius: 3 }}` existants
// sont multipliés par cette valeur, la changer ferait dériver tous les écrans. Les rayons des
// composants sont donc fixés ici explicitement, en px.

const R = { sm: 6, md: 8, lg: 12, xl: 14 };

// Ombres douces et courtes (au lieu des ombres Material marquées) — une seule échelle pour tout.
const shadows = [
  'none',
  '0 1px 2px rgba(16,24,40,0.05)',
  '0 1px 3px rgba(16,24,40,0.07), 0 1px 2px rgba(16,24,40,0.04)',
  '0 2px 6px rgba(16,24,40,0.07), 0 1px 2px rgba(16,24,40,0.04)',
  '0 4px 10px rgba(16,24,40,0.08), 0 1px 3px rgba(16,24,40,0.05)',
  '0 6px 14px rgba(16,24,40,0.09), 0 2px 4px rgba(16,24,40,0.05)',
  '0 8px 18px rgba(16,24,40,0.10), 0 2px 5px rgba(16,24,40,0.05)',
  '0 10px 22px rgba(16,24,40,0.10), 0 3px 6px rgba(16,24,40,0.05)',
  '0 12px 28px -4px rgba(16,24,40,0.14), 0 0 0 1px rgba(16,24,40,0.04)',
];
while (shadows.length < 25) shadows.push('0 24px 48px -12px rgba(16,24,40,0.28), 0 0 0 1px rgba(16,24,40,0.04)');

/**
 * Construit le thème MUI à partir des tokens COURANTS — à rappeler après `applyBrandColor()` (couleur de marque du
 * club, appliquée dans App.js) : `secondary`, `action.selected` et les sélections de menu / DataGrid en dépendent.
 *
 * `accentLed` (espace de la Ligue, src/league/theme) : la couleur de marque devient la couleur `primary` (boutons
 * principaux, champ actif, onglet actif, curseurs) et le noir `secondary` — charte de la Ligue. Par défaut (espace
 * club), `primary` reste le noir des libellés et la marque `secondary`, comme avant.
 */
export function buildTheme({ accentLed = false } = {}) {
  const focusRing = `0 0 0 3px ${tint(t.label, 0.12)}`;
  // Apparence sombre (theme/colorMode.js) : `t.label` devient clair, les boutons principaux passent en blanc sur noir.
  const dark = t.mode === 'dark';
  const ink = dark ? '#111318' : '#FFFFFF';
  // Couleur des éléments actifs (champ focalisé, onglet, curseur) : noir dans l'ERP, couleur de marque dans la Ligue.
  const activeColor = accentLed ? t.accent : t.label;

  // Textes des composants MUI (pagination, tri, filtres des DataGrid…) en français : frFR en 2e/3e argument.
  return createTheme({
    palette: {
      mode: dark ? 'dark' : 'light',
      ...(accentLed
        ? {
            primary: { main: t.accent, light: '#F0434F', dark: t.accentStrong, contrastText: t.accentInk },
            secondary: { main: t.label, light: dark ? '#FFFFFF' : '#2A2A2A', dark: dark ? '#C9CDD5' : '#000000', contrastText: ink },
          }
        : {
            primary: { main: t.label, light: dark ? '#FFFFFF' : '#2A2E37', dark: dark ? '#C9CDD5' : '#000000', contrastText: ink },
            secondary: { main: t.accent, dark: t.accentStrong, contrastText: t.accentInk },
          }),
      success: { main: t.status.played.color, contrastText: '#FFFFFF' },
      warning: { main: t.status.reported.color, contrastText: '#FFFFFF' },
      error: { main: t.status.injured.color, contrastText: '#FFFFFF' },
      info: { main: t.status.noShow.color, contrastText: '#FFFFFF' },
      background: { default: t.canvas, paper: t.surface },
      text: { primary: t.label, secondary: t.secondaryLabel, disabled: t.tertiaryLabel },
      divider: t.separator,
      action: { hover: t.surfaceHover, selected: tint(t.accent, 0.18), focus: tint(t.label, 0.08) },
    },
    shape: { borderRadius: 4 },
    shadows,
    // Variantes MUI = exactement l'échelle `appleTokens.type` (mêmes 9 tailles, mêmes graisses).
    typography: {
      fontFamily: t.fontFamily,
      fontSize: 14,
      fontWeightLight: 400,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 700,
      h1: { ...t.type.display },
      h2: { ...t.type.pageTitle },
      h3: { ...t.type.pageTitle },
      h4: { ...t.type.title },
      h5: { ...t.type.sectionTitle },
      h6: { ...t.type.sectionTitle },
      subtitle1: { ...t.type.bodyStrong },
      subtitle2: { ...t.type.smallStrong },
      body1: { ...t.type.body },
      body2: { ...t.type.small },
      caption: { ...t.type.caption },
      overline: { ...t.type.overline },
      button: { fontSize: '0.8125rem', fontWeight: 600, textTransform: 'none', letterSpacing: 0 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: t.canvas,
            color: t.label,
            fontFeatureSettings: '"cv11", "ss01"',
            WebkitFontSmoothing: 'antialiased',
          },
          '*::-webkit-scrollbar': { width: 10, height: 10 },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: tint(t.label, 0.16),
            borderRadius: 8,
            border: '2px solid transparent',
            backgroundClip: 'content-box',
          },
          '*::-webkit-scrollbar-thumb:hover': { backgroundColor: tint(t.label, 0.28) },
          '*::-webkit-scrollbar-track': { background: 'transparent' },
        },
      },

      // --- Boutons
      MuiButtonBase: { defaultProps: { disableRipple: false } },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: R.md,
            fontWeight: 600,
            boxShadow: 'none',
            whiteSpace: 'nowrap',
            '&:hover': { boxShadow: 'none' },
            '&.Mui-focusVisible': { boxShadow: focusRing },
          },
          sizeSmall: { height: 32, padding: '0 12px', fontSize: '0.8125rem' },
          sizeMedium: { height: 36, padding: '0 14px', fontSize: '0.8125rem' },
          sizeLarge: { height: 42, padding: '0 18px', fontSize: '0.875rem' },
          startIcon: { marginRight: 6, '& > *:nth-of-type(1)': { fontSize: 18 } },
          endIcon: { marginLeft: 6, '& > *:nth-of-type(1)': { fontSize: 18 } },
          containedPrimary: { '&:hover': { backgroundColor: dark ? '#FFFFFF' : '#000000' } },
          containedInherit: {
            backgroundColor: t.surfaceHover,
            color: t.label,
            '&:hover': { backgroundColor: t.separator },
          },
          outlined: {
            borderColor: t.separatorStrong,
            backgroundColor: t.surface,
            '&:hover': { backgroundColor: t.surfaceSunken, borderColor: dark ? '#4A505B' : '#C3C7CF' },
          },
          outlinedPrimary: { color: t.label },
          outlinedInherit: { borderColor: t.separatorStrong },
          text: { '&:hover': { backgroundColor: t.surfaceHover } },
          textPrimary: { color: t.label },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: R.md,
            '&:hover': { backgroundColor: t.surfaceHover },
            '&.Mui-focusVisible': { boxShadow: focusRing },
          },
          sizeSmall: { padding: 5 },
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: { backgroundColor: t.surfaceHover, borderRadius: R.md, padding: 2, gap: 2 },
          grouped: {
            border: 0,
            borderRadius: `${R.sm}px !important`,
            '&.Mui-selected': { backgroundColor: t.surface, boxShadow: shadows[1] },
            '&.Mui-selected:hover': { backgroundColor: t.surface },
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.8125rem',
            color: t.secondaryLabel,
            border: 0,
            padding: '4px 12px',
            '&.Mui-selected': { color: t.label },
          },
          sizeSmall: { padding: '3px 10px' },
        },
      },
      MuiFab: { styleOverrides: { root: { boxShadow: shadows[4] } } },

      // --- Surfaces
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { backgroundImage: 'none' },
          rounded: { borderRadius: R.lg },
          outlined: { borderColor: t.separator },
          elevation0: { border: `1px solid ${t.separator}` },
        },
      },
      MuiCard: {
        defaultProps: { variant: 'outlined' },
        styleOverrides: { root: { borderRadius: R.lg, borderColor: t.separator, boxShadow: 'none' } },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: { padding: '14px 18px', borderBottom: `1px solid ${t.separator}` },
          title: { fontSize: '0.9375rem', fontWeight: 700 },
          subheader: { fontSize: '0.8125rem', color: t.secondaryLabel },
        },
      },
      MuiCardContent: { styleOverrides: { root: { padding: 18, '&:last-child': { paddingBottom: 18 } } } },
      MuiAccordion: {
        defaultProps: { disableGutters: true },
        styleOverrides: {
          root: {
            border: `1px solid ${t.separator}`,
            borderRadius: `${R.lg}px !important`,
            boxShadow: 'none',
            '&::before': { display: 'none' },
            '& + &': { marginTop: 8 },
          },
        },
      },
      MuiAccordionSummary: { styleOverrides: { root: { minHeight: 48, fontWeight: 600 }, content: { margin: '12px 0' } } },
      MuiAppBar: { defaultProps: { elevation: 0 } },
      MuiDivider: { styleOverrides: { root: { borderColor: t.separator } } },

      // --- Overlays
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: R.xl,
            border: `1px solid ${t.separator}`,
            boxShadow: t.shadow.overlay,
            backgroundImage: 'none',
          },
          paperFullScreen: { borderRadius: 0, border: 0 },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: { '&:not(.MuiBackdrop-invisible)': { backgroundColor: dark ? 'rgba(0,0,0,0.6)' : 'rgba(15,17,21,0.32)', backdropFilter: 'blur(2px)' } },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: { fontSize: '1rem', fontWeight: 700, lineHeight: 1.35, padding: '18px 22px 14px', color: t.label },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: { padding: '8px 22px 18px' },
          dividers: { padding: '18px 22px', borderColor: t.separator },
        },
      },
      MuiDialogContentText: { styleOverrides: { root: { fontSize: '0.875rem', color: t.secondaryLabel } } },
      MuiDialogActions: {
        styleOverrides: {
          root: { padding: '12px 22px', gap: 8, borderTop: `1px solid ${t.separator}`, '& > :not(style) ~ :not(style)': { marginLeft: 0 } },
        },
      },
      MuiPopover: {
        styleOverrides: { paper: { borderRadius: R.lg, border: `1px solid ${t.separator}`, boxShadow: shadows[8] } },
      },
      MuiMenu: {
        styleOverrides: {
          paper: { borderRadius: R.lg, border: `1px solid ${t.separator}`, boxShadow: shadows[8] },
          list: { padding: 4 },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: '0.875rem',
            borderRadius: R.sm,
            minHeight: 34,
            '&.Mui-selected': { backgroundColor: tint(t.accent, 0.18) },
            '&.Mui-selected:hover': { backgroundColor: tint(t.accent, 0.26) },
          },
        },
      },
      MuiListItemIcon: { styleOverrides: { root: { minWidth: 32 } } },
      MuiTooltip: {
        defaultProps: { arrow: false },
        styleOverrides: {
          tooltip: { backgroundColor: dark ? '#2F333C' : '#1B1E24', fontSize: '0.75rem', fontWeight: 500, borderRadius: R.sm, padding: '5px 9px' },
        },
      },
      MuiDrawer: { styleOverrides: { paper: { borderColor: t.separator } } },
      MuiSnackbar: {},

      // --- Formulaires
      MuiTextField: { defaultProps: { size: 'small' } },
      MuiFormControl: { defaultProps: { size: 'small' } },
      MuiAutocomplete: {
        defaultProps: { size: 'small' },
        styleOverrides: { paper: { borderRadius: R.lg, border: `1px solid ${t.separator}`, boxShadow: shadows[8] } },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: R.md,
            backgroundColor: t.surface,
            fontSize: '0.875rem',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: t.separatorStrong },
            '&:hover:not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline': { borderColor: dark ? '#4A505B' : '#B8BDC6' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: activeColor, borderWidth: 1 },
            '&.Mui-focused': { boxShadow: focusRing },
            '&.Mui-disabled': { backgroundColor: t.surfaceSunken },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: { fontSize: '0.875rem', color: t.secondaryLabel, '&.Mui-focused': { color: t.label } },
        },
      },
      MuiFormLabel: { styleOverrides: { root: { '&.Mui-focused': { color: t.label } } } },
      MuiFormHelperText: { styleOverrides: { root: { fontSize: '0.75rem', marginLeft: 2, marginRight: 2 } } },
      MuiInputAdornment: { styleOverrides: { root: { color: t.tertiaryLabel } } },
      MuiSelect: { styleOverrides: { icon: { color: t.tertiaryLabel } } },
      MuiCheckbox: { defaultProps: { size: 'small' }, styleOverrides: { root: { color: t.separatorStrong } } },
      MuiRadio: { defaultProps: { size: 'small' }, styleOverrides: { root: { color: t.separatorStrong } } },
      MuiFormControlLabel: { styleOverrides: { label: { fontSize: '0.875rem' } } },
      MuiSwitch: {
        styleOverrides: {
          root: { width: 44, height: 26, padding: 0, margin: 6 },
          switchBase: {
            padding: 3,
            '&.Mui-checked': {
              transform: 'translateX(18px)',
              color: '#FFFFFF',
              '& + .MuiSwitch-track': { backgroundColor: t.status.played.color, opacity: 1 },
            },
            '&.Mui-disabled + .MuiSwitch-track': { opacity: 0.4 },
          },
          thumb: { width: 20, height: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.2)' },
          track: { borderRadius: 13, backgroundColor: t.separatorStrong, opacity: 1 },
          sizeSmall: {
            width: 36,
            height: 20,
            '& .MuiSwitch-switchBase': { padding: 2, '&.Mui-checked': { transform: 'translateX(16px)' } },
            '& .MuiSwitch-thumb': { width: 16, height: 16 },
            '& .MuiSwitch-track': { borderRadius: 10 },
          },
        },
      },
      MuiSlider: { styleOverrides: { root: { color: activeColor } } },

      // --- Données
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: R.sm, fontWeight: 600, fontSize: '0.75rem' },
          sizeSmall: { height: 22 },
          label: { paddingLeft: 8, paddingRight: 8 },
          outlined: { borderColor: t.separatorStrong },
          filled: { '&.MuiChip-colorDefault': { backgroundColor: t.surfaceHover, color: t.label } },
        },
      },
      MuiBadge: { styleOverrides: { badge: { fontWeight: 700, fontSize: '0.6875rem' } } },
      MuiAvatar: { styleOverrides: { root: { fontWeight: 700, fontSize: '0.8125rem' } } },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: R.md, fontSize: '0.8125rem', alignItems: 'center', border: '1px solid transparent' },
          standardSuccess: { borderColor: tint(t.status.played.color, 0.22), backgroundColor: tint(t.status.played.color, 0.07) },
          standardInfo: { borderColor: tint(t.status.noShow.color, 0.22), backgroundColor: tint(t.status.noShow.color, 0.06) },
          standardWarning: { borderColor: tint(t.status.reported.color, 0.28), backgroundColor: tint(t.status.reported.color, 0.08) },
          standardError: { borderColor: tint(t.status.injured.color, 0.22), backgroundColor: tint(t.status.injured.color, 0.06) },
          message: { padding: '7px 0' },
          icon: { padding: '7px 0' },
        },
      },
      MuiLinearProgress: {
        styleOverrides: { root: { borderRadius: 4, backgroundColor: t.surfaceHover }, bar: { borderRadius: 4 } },
      },
      MuiCircularProgress: { defaultProps: { color: 'inherit' } },
      MuiSkeleton: { styleOverrides: { root: { backgroundColor: tint(t.label, 0.06) }, rounded: { borderRadius: R.md } } },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 42, borderBottom: `1px solid ${t.separator}` },
          indicator: { height: 2, borderRadius: 2, backgroundColor: activeColor },
          flexContainer: { gap: 20 },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            minHeight: 42,
            minWidth: 0,
            padding: '8px 2px',
            color: t.secondaryLabel,
            '&.Mui-selected': { color: t.label },
            '&:hover': { color: t.label },
          },
        },
      },
      MuiTableContainer: { styleOverrides: { root: { borderRadius: R.lg } } },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: t.separator, fontSize: '0.8125rem', padding: '10px 14px' },
          head: {
            backgroundColor: t.surfaceSunken,
            color: t.secondaryLabel,
            fontSize: '0.75rem',
            fontWeight: 600,
            whiteSpace: 'nowrap',
          },
          sizeSmall: { padding: '7px 12px' },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: { '&.MuiTableRow-hover:hover': { backgroundColor: t.surfaceSunken } },
        },
      },
      MuiTablePagination: { styleOverrides: { root: { fontSize: '0.8125rem' } } },
      MuiPaginationItem: { styleOverrides: { root: { borderRadius: R.md } } },
      MuiBreadcrumbs: { styleOverrides: { root: { fontSize: '0.8125rem' }, separator: { color: t.tertiaryLabel } } },
      MuiLink: { defaultProps: { underline: 'hover' }, styleOverrides: { root: { color: t.label, fontWeight: 600 } } },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            '--DataGrid-containerBackground': t.surfaceSunken,
            '--DataGrid-rowBorderColor': t.separator,
            borderColor: t.separator,
            borderRadius: R.lg,
            backgroundColor: t.surface,
            fontSize: '0.8125rem',
            color: t.label,
            '& .MuiDataGrid-columnHeader': { backgroundColor: t.surfaceSunken },
            '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600, fontSize: '0.75rem', color: t.secondaryLabel },
            '& .MuiDataGrid-columnSeparator': { color: t.separator },
            '& .MuiDataGrid-row:hover': { backgroundColor: t.surfaceSunken },
            '& .MuiDataGrid-row.Mui-selected': { backgroundColor: tint(t.accent, 0.18) },
            '& .MuiDataGrid-row.Mui-selected:hover': { backgroundColor: tint(t.accent, 0.26) },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
              { outline: 'none' },
            '& .MuiDataGrid-footerContainer': { borderColor: t.separator, minHeight: 44 },
            '& .MuiDataGrid-toolbarContainer': { padding: '8px 10px', gap: 8 },
          },
        },
      },
    },
  }, dataGridFrFR, coreFrFR);
}

/** Thème avec les tokens au chargement du module (couleur de marque en cache, sinon jaune par défaut). */
const theme = buildTheme();

export default theme;
